import dayjs from 'dayjs';
import { GLOBAL_TIME_RANGE_FORMAT } from '@/common/components/molecules/timeController';
import type { TimePeriodInfo } from '@/common/components/molecules/timeController/types';
import { getTimePeriodByRange } from '@/common/utils/core/time/stringify';
import { utcZeroTimeToStandardTime } from '@/common/utils/commonUtils';

/**
 * 외부(대시보드/PA 등)에서 디테일 연계 시 어댑터가 panel.addition에 실어 보낸 UTC 기준
 * fromTime/toTime을 TimePeriodInfo로 변환.
 * timePeriod는 `getTimePeriodByRange`로 임의 범위(`p12m`, `p5h`, `p1d 2h 30m` 등)까지 라벨화.
 */
export const buildLinkedTimePeriodInfo = (
  fromTimeUtc: string | undefined,
  toTimeUtc: string | undefined,
): TimePeriodInfo | undefined => {
  if (!fromTimeUtc || !toTimeUtc) return undefined;
  return {
    timePeriod: getTimePeriodByRange(fromTimeUtc, toTimeUtc),
    fromTime: utcZeroTimeToStandardTime(fromTimeUtc, GLOBAL_TIME_RANGE_FORMAT),
    toTime: utcZeroTimeToStandardTime(toTimeUtc, GLOBAL_TIME_RANGE_FORMAT),
    fromTimeUtc,
    toTimeUtc,
    isPaused: true,
  };
};

/**
 * 외부 시간이 화면의 최대 조회 기간(maxDurationSec)을 넘으면 toTime 기준 maxDurationSec 전을
 * fromTime으로 잘라낸 새 TimePeriodInfo 반환. 이내면 그대로 반환.
 * 최대 조회 기간 제약이 있는 화면에서 사용.
 */
export const clampLinkedTimePeriodInfo = (
  info: TimePeriodInfo,
  maxDurationSec: number,
): TimePeriodInfo => {
  const fromMs = dayjs(info.fromTimeUtc).valueOf();
  const toMs = dayjs(info.toTimeUtc).valueOf();
  const diffSec = (toMs - fromMs) / 1000;
  if (diffSec <= maxDurationSec) return info;

  const clampedFromTimeUtc = dayjs(info.toTimeUtc)
    .subtract(maxDurationSec, 'second')
    .format('YYYY-MM-DD HH:mm:ss');
  return buildLinkedTimePeriodInfo(clampedFromTimeUtc, info.toTimeUtc) ?? info;
};

/**
 * 외부 시간 범위가 thresholdSec 미만이면 toTime 기준으로 targetSec 전을 fromTime으로 늘린
 * 새 TimePeriodInfo 반환. 이상이면 그대로 반환. targetSec 미지정 시 thresholdSec와 동일.
 * 최소 조회 기간 제약이 있는 화면에서 사용.
 * 최소 조회 기간 제약 걸릴 때 최소 조회 기간 말고 다른 조회 기간으로 표현하고 싶다면 targetSec 인자 사용.
 */
export const expandLinkedTimePeriodInfo = (
  info: TimePeriodInfo,
  thresholdSec: number,
  targetSec: number = thresholdSec,
): TimePeriodInfo => {
  const fromMs = dayjs(info.fromTimeUtc).valueOf();
  const toMs = dayjs(info.toTimeUtc).valueOf();
  const diffSec = (toMs - fromMs) / 1000;
  if (diffSec >= thresholdSec) return info;

  const expandedFromTimeUtc = dayjs(info.toTimeUtc)
    .subtract(targetSec, 'second')
    .format('YYYY-MM-DD HH:mm:ss');
  return buildLinkedTimePeriodInfo(expandedFromTimeUtc, info.toTimeUtc) ?? info;
};
